import "./styles.less";

import { Form, Modal, Space, Tooltip, message } from "antd";
import {
	CheckOutlined,
	CopyOutlined,
	FacebookFilled,
	PrinterFilled,
	ShareAltOutlined,
	TwitterSquareFilled,
	WhatsAppOutlined,
	YoutubeFilled,
} from "@ant-design/icons";
import React, { useContext, useEffect, useState } from "react";

import { ConfigStateContext } from "../../../Contexts/Configurations/context";
import { PropComponentMode } from "../PropertyInterfaces";
import { useRouter } from "next/router";
import { useSocialShare } from "./hook";
import { useUser } from "../../User/User.hook";
import { CloseButton } from "shared-components/Components/CustomIcons/CloseButton/web";
import usePropertyData from "src/hooks/propertyData/usePropertyData";
import { Button } from "shared-components/Components/Buttons/web";

const socialMediaIcons = Object.freeze({
	YouTube: YoutubeFilled,
	Facebook: FacebookFilled,
	Twitter: TwitterSquareFilled,
	WhatsApp: WhatsAppOutlined,
	Copy: CopyOutlined,
	Check: CheckOutlined,
	Print: PrinterFilled,
});
interface ShareIconProps {
	size?: any;
	color?: string;
	withCircles?: boolean;
  }

const ShareIcon: React.FC<ShareIconProps> = ({ size = 28, color = "#FFFFFF", withCircles = false }) => {
	return (
	  <svg
		viewBox="64 64 896 896"
		focusable="false"
		data-icon="share-alt"
		width={size}
		height={size}
		fill={color}
		aria-hidden="true"
	  >
		{withCircles && <circle cx="312" cy="512" r="88" fill="#000" fillOpacity={0.3}/>}
		{withCircles && <circle cx="752" cy="240" r="52" fill="#000" fillOpacity={0.3}/>}
		{withCircles && <circle cx="752" cy="784" r="52" fill="#000" fillOpacity={0.3}/>}
		<path d="M752 664c-28.5 0-54.8 10-75.4 26.7L469.4 540.8a160.68 160.68 0 000-57.6l207.2-149.9C697.2 350 723.5 360 752 360c66.2 0 120-53.8 120-120s-53.8-120-120-120-120 53.8-120 120c0 11.6 1.6 22.7 4.7 33.3L439.9 415.8C410.7 377.1 364.3 352 312 352c-88.4 0-160 71.6-160 160s71.6 160 160 160c52.3 0 98.7-25.1 127.9-63.8l196.8 142.5c-3.1 10.6-4.7 21.8-4.7 33.3 0 66.2 53.8 120 120 120s120-53.8 120-120-53.8-120-120-120zm0-476c28.7 0 52 23.3 52 52s-23.3 52-52 52-52-23.3-52-52 23.3-52 52-52zM312 600c-48.5 0-88-39.5-88-88s39.5-88 88-88 88 39.5 88 88-39.5 88-88 88zm440 236c-28.7 0-52-23.3-52-52s23.3-52 52-52 52 23.3 52 52-23.3 52-52 52z"/>
	  </svg>
	);
  };

const renderIcon = (Icon, fallback = null, props = {}) => {
	return Icon ? <Icon {...props} /> : fallback;
};

export function SocialShare({
	id,
	mode = "auto",
	isEspecial,
	isDisabledProperty = false,
	iconWidth = 28,
	type = "default",
	iconColor = "#FFFFFF",
	withCircles = false,
}: {
	id: any;
	mode?: PropComponentMode;
	isEspecial?: Boolean;
	isDisabledProperty?: boolean;
	iconWidth?: number;
	type?: "default" | "justIcon";
	iconColor?: string;
	withCircles?: boolean;
}) {
	const { listing } = usePropertyData();

	const handleClickShowNotification = async ({
		type = "success",
		message = "",
		description = "",
	}) => {
		const { showNotification } = await import(
			"shared-components/Components/CustomNotification/web"
		);
		showNotification(type, message, description);
	};
	const {
		loading,
		error,
		socialMediaLink,
		shareByEmail: { share, loading: shareLoading, data: shareData },
	} = useSocialShare({ id, mode });

	const { isLoggedIn, user } = useUser();
	const { asPath } = useRouter();
	const { site_name, main_domain } = useContext(ConfigStateContext);

	const [modal, setModal] = useState(false);
	const [form] = Form.useForm();

	const onCreate = values => {
		share(values)
			.then(data => {
				const description =
					"La propiedad ha sido compartida exitosamente. Le enviamos esta propiedad a " +
					values.other;
				handleClickShowNotification({
					message: "Propiedad Compartida",
					description,
				});
			})
			.catch(data => {
				handleClickShowNotification({
					message: "Ha ocurrido un error",
					description: "Ocurrió un error compartiendo esta propiedad",
					type: "error",
				});
			});

		setModal(false);
	};

	useEffect(() => {
		if (isLoggedIn) form.setFieldsValue({ me: user.data?.me.email });
	}, [isLoggedIn, user]);

	const CollectionCreateForm = ({ visible, onCreate, onCancel }) => {
		return (
			<Modal
				className="social-share-modal"
				visible={visible}
				footer={null}
				closeIcon={CloseButton ? <CloseButton /> : null}
				onCancel={onCancel}
				title="Compartir Propiedad"
				centered
				width="100%"
			>
				<Form
					onFinish={onCreate}
					requiredMark={false}
					form={form}
					layout="vertical"
					name="form_in_modal"
					scrollToFirstError
				>
					<Form.Item className="social-share-networks">
						{listing?.socialMediaLinks && (
							<Space>
								<Tooltip title="Facebook">
									<a
										className="button button-secondary secondary"
										href={listing?.socialMediaLinks[0]?.url} // el enlace viene con la API de facebook
										target={"_blank"}
									>{renderIcon(socialMediaIcons.Facebook)}</a>
								</Tooltip>
								<Tooltip title="Twitter">
									<a
										className="button button-secondary secondary"
										href={`https://twitter.com/intent/tweet?text=${encodeURI(
											`Mira esta propiedad de ${site_name}`
										)}&url=${listing?.socialMediaLinks[2]?.url}&hashtags=${site_name},${"Propiedad"}`}
										target={"_blank"}
									>{renderIcon(socialMediaIcons.Twitter)}</a>
								</Tooltip>
								<Tooltip title="WhatsApp">
									<a
										className="button button-secondary secondary"
										href={`https://wa.me/?text=${encodeURI(
											`Mira esta propiedad de ${site_name}: \n https://${main_domain}${listing?.link}`
										)}`}
										target={"_blank"}
									>{renderIcon(socialMediaIcons.WhatsApp)}</a>
								</Tooltip>
								{mode != "project" && (
									<a
										className="button button-secondary secondary"
										href={`${asPath}?imprimir`}
										target={"_blank"}
									>{renderIcon(socialMediaIcons.Print)}</a>
								)}
								<a
									className="button button-secondary secondary"
									onClick={(e) => {
										e.preventDefault();
										if (typeof navigator.clipboard !== "undefined") {
											navigator.clipboard
												.writeText(`https://${main_domain}${listing?.link}`)
												.then(() => {
													message.success({
														icon: renderIcon(socialMediaIcons.Check),
														content: "¡Copiado!",
													});
												});
										} else {
											const el = document.createElement("textarea");
											el.value = socialMediaLink;
											document.body.appendChild(el);
											el.select();
											document.execCommand("copy");
											document.body.removeChild(el);
										}
									}}
								>{renderIcon(socialMediaIcons.Copy)}</a>
							</Space>
						)}
					</Form.Item>
				</Form>
			</Modal>
		);
	};

	return (
		<div>
			{Button ? (
				<Button
					disabled={isDisabledProperty}
					className={`social-share-button button button-secondary secondary ${
						isEspecial ? "isEspecial" : ""
					}`}
					onClick={e => {
						e.stopPropagation();
						setModal(true);
					}}
					type={type === "justIcon" ? "noStyle" : "secondary"}>
					{type === "justIcon" ? (
						<ShareAltOutlined style={{ fontSize: iconWidth, color: iconColor }} />
					) : (
						renderIcon(ShareAltOutlined, <ShareIcon size={iconWidth} color={iconColor} withCircles={withCircles} />, { className: "social-share-icon" })
					)}
					{!isEspecial && type !== "justIcon" ? "Compartir" : null}
				</Button>
			) : (
				<button
					disabled={isDisabledProperty}
					className={`social-share-button button button-secondary secondary ${
						isEspecial ? "isEspecial" : ""
					}`}
					onClick={e => {
						e.stopPropagation();
						setModal(true);
					}}>
					{type === "justIcon" ? (
						<ShareAltOutlined style={{ fontSize: iconWidth, color: iconColor }} />
					) : (
						renderIcon(ShareAltOutlined, <ShareIcon size={iconWidth} color={iconColor} withCircles={withCircles} />, { className: "social-share-icon" })
					)}
					{!isEspecial && type !== "justIcon" ? "Compartir" : null}
				</button>
			)}
			<CollectionCreateForm
				visible={modal}
				onCreate={onCreate}
				onCancel={() => setModal(false)}
			/>
		</div>
	);
}
